import { PageShell } from '../components/layout/PageShell'
import { AnalyticsEvents } from '../firebase';
import { useSearchParams } from 'react-router-dom'

const SIGN_UP_URL = 'https://app.acesense.io/sign-up'

/**
 * Desktop chooser for users who hit /link from a non-mobile UA.
 * Store badges MUST go through /link?platform=… so the second hop is logged
 * (picked=true → App Store / Play). Preserve invite params `code` + `by`.
 */
export default function GetAppPage() {
  const [params] = useSearchParams()
  const code = params.get('code')?.trim() || 'get'
  const by = params.get('by')?.trim() || 'site'

  function storeHref(platform: 'ios' | 'android') {
    const qs = new URLSearchParams({ platform })
    // Only embed code/by when the URL actually carried them. Prerender visits
    // bare /get; embedding defaults (get/site) would override Referer inheritance
    // in downloadRedirect and attribute picks to the wrong campaign.
    if (params.get('code')?.trim()) qs.set('code', code)
    if (params.get('by')?.trim()) qs.set('by', by)
    return `/link?${qs.toString()}`
  }

  return (
    <PageShell
      title="Join the AceSense beta"
      description="Create a free AceSense account to join the beta, then download on iPhone or Android."
      canonical="/get"
    >
      <section
        style={{
          maxWidth: 560,
          margin: '0 auto',
          padding: '4rem 1.25rem 5rem',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            fontSize: 13,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            margin: '0 0 0.75rem',
          }}
        >
          Private beta
        </p>
        <h1
          style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.4rem)',
            letterSpacing: '-0.02em',
            margin: '0 0 0.75rem',
            color: '#f7f7f5',
          }}
        >
          Create a free account
        </h1>
        <p
          style={{
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.55,
            margin: '0 0 1.5rem',
          }}
        >
          Beta access starts with a free account. Sign up, then download the app
          for your phone.
        </p>
        <a
          href={SIGN_UP_URL}
          onClick={() => AnalyticsEvents.webAppClick('getapp-signup')}
          className="btn btn-primary btn-large"
          style={{ width: 'min(100%, 320px)', margin: '0 auto 0.35rem' }}
          aria-label="Create a free AceSense account to join the beta"
        >
          Create free account
        </a>
        <p
          style={{
            margin: '0 0 2rem',
            fontSize: 13,
            color: 'rgba(255,255,255,0.55)',
          }}
        >
          Required for beta access
        </p>
        <p
          style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.7)',
            margin: '0 0 1rem',
          }}
        >
          Then download the app
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.85rem',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <a
            href={storeHref('ios')}
            onClick={() => AnalyticsEvents.appStoreClick('getapp-ios')}
            className="app-store-link"
            aria-label="Download AceSense on the App Store"
            data-track="store-pick"
            data-platform="ios"
          >
            <img
              src="/app-store-badge.svg"
              alt="Download on the App Store"
              className="app-store-badge"
              height={56}
            />
          </a>
          <a
            href={storeHref('android')}
            onClick={() => AnalyticsEvents.appStoreClick('getapp-android')}
            className="app-store-link"
            aria-label="Get AceSense for Android on Google Play"
            data-track="store-pick"
            data-platform="android"
          >
            <img
              src="/google-play-badge.png"
              alt="Get it on Google Play"
              className="app-store-badge"
              height={56}
            />
          </a>
        </div>
        <p
          style={{
            marginTop: '1.25rem',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: 'left',
          }}
        >
          <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Android:</strong>{' '}
          Use the Google account tied to your AceSense signup. Can&rsquo;t join? Email{' '}
          <a
            href="mailto:admin@acesense.io"
            style={{ color: '#ccff00', textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            admin@acesense.io
          </a>
          .
        </p>
      </section>
    </PageShell>
  )
}
